import type { Facing } from '../contracts/presentation-snapshot';
import type { LocalAabb } from './aabb';
import { getAabbOverlap, toWorldAabb } from './aabb';
import type { StageBounds } from './spatial-types';
import { assertStageBounds } from './spatial-types';
import type { MutableVec2 } from './vec2';
import { assertFiniteVec2 } from './vec2';

export const MAX_WALL_RESOLUTION_PASSES = 4;

export class SpatialResolutionError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'SpatialResolutionError';
  }
}

export interface PushBody {
  readonly player: 0 | 1;
  readonly position: MutableVec2;
  facing: Facing;
  readonly pushBox: LocalAabb;
}

export const clampBodyToStage = (body: PushBody, stage: StageBounds): boolean => {
  const world = toWorldAabb(body.pushBox, body.position, body.facing);
  const width = world.maxX - world.minX;
  if (width > stage.maxX - stage.minX) throw new SpatialResolutionError('Push box is wider than the stage.');
  let correction = 0;
  if (world.minX < stage.minX) correction = stage.minX - world.minX;
  else if (world.maxX > stage.maxX) correction = stage.maxX - world.maxX;
  body.position.x += correction;
  return correction !== 0;
};

const orderedBodies = (bodies: readonly [PushBody, PushBody]): readonly [PushBody, PushBody] => {
  const [first, second] = bodies;
  if (first.position.x < second.position.x) return [first, second];
  if (first.position.x > second.position.x) return [second, first];
  return first.player < second.player ? [first, second] : [second, first];
};

export const resolveBodyPushAndWalls = (
  bodies: readonly [PushBody, PushBody],
  stage: StageBounds,
): number => {
  assertStageBounds(stage);
  for (const body of bodies) assertFiniteVec2(body.position, `player${body.player + 1}.position`);
  const [initialLeft, initialRight] = orderedBodies(bodies);
  const initialOverlap = getAabbOverlap(
    toWorldAabb(initialLeft.pushBox, initialLeft.position, initialLeft.facing),
    toWorldAabb(initialRight.pushBox, initialRight.position, initialRight.facing),
  );
  if (initialOverlap.x > 0 && initialOverlap.y > 0) {
    initialLeft.position.x -= initialOverlap.x / 2;
    initialRight.position.x += initialOverlap.x / 2;
  }
  for (let pass = 1; pass <= MAX_WALL_RESOLUTION_PASSES; pass += 1) {
    for (const body of bodies) clampBodyToStage(body, stage);
    const [left, right] = orderedBodies(bodies);
    const leftWorld = toWorldAabb(left.pushBox, left.position, left.facing);
    const rightWorld = toWorldAabb(right.pushBox, right.position, right.facing);
    const overlap = getAabbOverlap(leftWorld, rightWorld);
    if (overlap.x <= 0 || overlap.y <= 0) return pass;

    const leftRoom = leftWorld.minX - stage.minX;
    const rightRoom = stage.maxX - rightWorld.maxX;
    let moveLeft = Math.min(overlap.x / 2, Math.max(0, leftRoom));
    let moveRight = Math.min(overlap.x / 2, Math.max(0, rightRoom));
    let residual = overlap.x - moveLeft - moveRight;
    if (residual > 0) {
      const extraLeft = Math.min(residual, Math.max(0, leftRoom - moveLeft));
      moveLeft += extraLeft;
      residual -= extraLeft;
    }
    if (residual > 0) {
      const extraRight = Math.min(residual, Math.max(0, rightRoom - moveRight));
      moveRight += extraRight;
      residual -= extraRight;
    }
    if (residual > 0) throw new SpatialResolutionError('Stage geometry cannot resolve body overlap.');
    left.position.x -= moveLeft;
    right.position.x += moveRight;
  }
  const [left, right] = orderedBodies(bodies);
  const overlap = getAabbOverlap(
    toWorldAabb(left.pushBox, left.position, left.facing),
    toWorldAabb(right.pushBox, right.position, right.facing),
  );
  if (overlap.x > 0 && overlap.y > 0) throw new SpatialResolutionError('Body overlap did not converge within four passes.');
  return MAX_WALL_RESOLUTION_PASSES;
};

export const updateFacing = (bodies: readonly [PushBody, PushBody]): void => {
  const [playerOne, playerTwo] = [...bodies].sort(
    (left, right) => left.player - right.player,
  ) as [PushBody, PushBody];
  if (playerOne.position.x < playerTwo.position.x) {
    playerOne.facing = 'RIGHT';
    playerTwo.facing = 'LEFT';
  } else if (playerOne.position.x > playerTwo.position.x) {
    playerOne.facing = 'LEFT';
    playerTwo.facing = 'RIGHT';
  }
};
